import { useCallback, useRef } from "react";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import type { SearchResponse } from "@/features/search/api/queries";
import type { SearchRoute } from "@/features/search/api/route";
import {
  getSearchListId,
  toAnalyticsProduct,
} from "@/features/search/utils/analytics.utils";
import type { UseQueryResult } from "@tanstack/react-query";

/**
 * The two events a result set fires on its own: `search` and `view_item_list`.
 *
 * Both run through `useExecAfterMount`, matching the gateway PLP — the events are client-side, and
 * firing during render would double up under React's strict mode.
 *
 * `search` needs a query to be meaningful, and the service drops the call when there isn't one, so
 * a curated landing page reports `view_item_list` alone. That is also what the old page did.
 *
 * Placeholder data is skipped: while a refinement is in flight the previous page's results are
 * still on screen, and reporting them again would count one list view per keystroke.
 *
 * Both are reported per *result set*, not per `data` object: favoriting writes `favoriteId` back
 * into every cached page, so a heart click hands these effects a brand-new object for a list that
 * did not change. Without the signature guard one favorite re-fires both events (twice, for an
 * unfavorite — the optimistic write and then the revert).
 */
export const useSearchAnalytics = (
  route: SearchRoute,
  query: UseQueryResult<SearchResponse>
) => {
  const data = query.isPlaceholderData ? undefined : query.data;
  /* Only a query search has one — `/pages` takes no `query`, so a landing page has nothing to report
   * as the search term, and `trackSearch` drops the call rather than sending an empty one. */
  const searchTerm = route.kind === "search" ? route.params.query : undefined;
  /* What the events actually describe: which results came back, in which order. */
  const signature = data
    ? `${searchTerm}|${data.pageInfo.total}|${data.data
        .map(product => product.id)
        .join(",")}`
    : undefined;
  const reportedSearch = useRef<string | undefined>(undefined);
  const reportedList = useRef<string | undefined>(undefined);

  analyticsService.useExecAfterMount(
    useCallback(() => {
      if (!data || reportedSearch.current === signature) {
        return;
      }

      reportedSearch.current = signature;

      /* `defaultSearch` is true: this page has no image search, so it is always the plain
       * Whoppah search. */
      analyticsService.trackSearch(
        searchTerm,
        data.pageInfo.total,
        undefined,
        true
      );
    }, [data, searchTerm, signature])
  );

  analyticsService.useExecAfterMount(
    useCallback(() => {
      if (!data?.data.length || reportedList.current === signature) {
        return;
      }

      reportedList.current = signature;

      const listId = getSearchListId(data);

      analyticsService.trackViewItemList(
        listId,
        listId,
        data.data.map(toAnalyticsProduct)
      );
    }, [data, signature])
  );
};
